import { useEffect, useRef } from "react";

/**
 * Hook theo dõi hướng scroll của 1 element (hoặc window)
 * 
 * @param {Object} options
 * @param {React.RefObject | null} options.ref - ref của element muốn lắng nghe (nếu null => window)
 * @param {(direction: "up" | "down", position: number) => void} options.onSwipe - callback khi scroll đổi hướng
 * @param {boolean} [options.enable=true] - bật/tắt theo dõi
 * @param {number} [options.threshold=10] - khoảng cách tối thiểu để tính là đổi hướng
 * @param {number} [options.bottomOffset=0] - khoảng cách đến cuối để bỏ qua (Đã đưa về 0 vì không cần hack fix bounce nữa)
 */
export function useScrollDirection({
  ref,
  onSwipe,
  enable = true,
  threshold = 10,
  bottomOffset = 0, // Lưu ý: Bạn có thể giảm giá trị 800 cũ xuống
}) {
  const prevScrollPos = useRef(0);

  useEffect(() => {
    if (!enable || !onSwipe) {
      return;
    }
    const target = ref?.current || window;

    const handleScroll = () => {
      const el = ref?.current || document.documentElement || document.body;

      // 1. Lấy toạ độ cuộn hiện tại
      const currentScrollPos = ref?.current
        ? el.scrollTop
        : window.scrollY || document.documentElement.scrollTop;

      // 2. Lấy chiều cao của khung nhìn (viewport)
      const clientHeight = ref?.current
        ? el.clientHeight
        : window.innerHeight || document.documentElement.clientHeight;

      // 3. Lấy tổng chiều cao nội dung cuộn
      const scrollHeight = ref?.current
        ? el.scrollHeight
        : document.documentElement.scrollHeight;

      // Tính toạ độ cuộn tối đa (điểm đáy thực sự của trang/element)
      const maxScroll = Math.max(0, scrollHeight - clientHeight);

      // ==========================================
      // FIX HIỆU ỨNG BOUNCE (RUBBER-BAND)
      // ==========================================
      // Nếu cuộn lố lên trên (<0) hoặc lố xuống dưới đáy (>maxScroll)
      if (currentScrollPos <= 0 || currentScrollPos >= maxScroll) {
        // Clamp giá trị prevScrollPos nằm gọn trong khoảng [0, maxScroll] 
        // để khi trình duyệt tự "bật" lại, khoảng diff sẽ không đủ lớn để kích hoạt nhầm.
        prevScrollPos.current = Math.max(0, Math.min(currentScrollPos, maxScroll));
        return;
      }

      const diff = prevScrollPos.current - currentScrollPos;

      // Bỏ qua nếu sự thay đổi nhỏ hơn threshold (lưu ý: không cập nhật prevScrollPos ở đây để nó có thể cộng dồn)
      if (Math.abs(diff) < threshold) {
        return;
      }

      // Xử lý bottomOffset (nếu bạn vẫn muốn vô hiệu hóa thanh điều hướng khi ở rất gần đáy)
      if (maxScroll - currentScrollPos < bottomOffset) {
        // QUAN TRỌNG: Phải cập nhật prevScrollPos trước khi return, 
        // nếu không sẽ bị lỗi tính toán diff ở lần scroll lên tiếp theo.
        prevScrollPos.current = currentScrollPos;
        return;
      }

      const direction = diff > 0 ? "down" : "up";
      onSwipe(direction, currentScrollPos);

      // Lưu lại vị trí cuộn mới nhất
      prevScrollPos.current = currentScrollPos;
    };

    target.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => target.removeEventListener("scroll", handleScroll);

  }, [ref, onSwipe, enable, threshold, bottomOffset]);
}